import {
  Text,
  Code,
  Paper,
  Badge,
  Stack,
  Group,
  NumberFormatter,
  ScrollAreaAutosize,
} from "@mantine/core";
import Markdown from "../_Common/Markdown";
import CopyTextActionIcon from "../Playground/CopyTextActionIcon";
import JsonBlock from "../_Common/JsonBlock";
import { IconStopwatch } from "@tabler/icons-react";

function HttpStatusBadge({ statusCode, ...props }) {
  let color = "blue";
  if (statusCode >= 500) color = "red";
  if (statusCode >= 400) color = "orange";
  if (statusCode >= 300) color = "yellow";
  return (
    <Badge size="xs" variant="outline" color={color} {...props}>
      {statusCode}
    </Badge>
  );
}

function ContentPartText({ part }) {
  // TBD: Introduce Context
  const displayMode = "mono";
  return (
    <ScrollAreaAutosize mah={300}>
      {displayMode === "markdown" && (
        <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          <Markdown>{part.text}</Markdown>
        </div>
      )}
      {displayMode === "mono" && (
        <JsonBlock
          value={part.text}
          style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
        />
      )}
      {displayMode === "raw" && (
        <Text style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {part.text}
        </Text>
      )}
    </ScrollAreaAutosize>
  );
}

function ContentPartFunctionCall({ part }) {
  return (
    <ScrollAreaAutosize mah={300}>
      <JsonBlock value={part.toolCall} />
    </ScrollAreaAutosize>
  );
}

function ContentPartFunctionResult({ part }) {
  return (
    <ScrollAreaAutosize mah={300}>
      <JsonBlock value={part.toolCallResult} />
    </ScrollAreaAutosize>
  );
}

function ContentPartImage({ part, request }) {
  var fetch = request?.imageFetches?.find((f) => f.url === part.url);

  if (!fetch)
    return (
      <div>
        <Code style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}>{part.url}</Code>
      </div>
    );

  return (
    <div>
      <Group fz="xs" mt="xs">
        <HttpStatusBadge
          statusCode={fetch.statusCode}
          size="xs"
          variant="outline"
        />
        <Badge
          leftSection={<IconStopwatch size={12} />}
          size="xs"
          radius="sm"
          variant="default"
        >
          <NumberFormatter
            value={fetch.milliseconds}
            thousandSeparator
            suffix=" ms"
          />
        </Badge>
        <NumberFormatter
          value={fetch.bytes}
          thousandSeparator
          suffix=" bytes"
        />
        <Code style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}>{fetch.url}</Code>
      </Group>
    </div>
  );
}

function MessageDetails({ message, request }) {
  let content = message.content;
  if (!content || content.length === 0)
    content = [{ type: "text", text: message.text }];

  return content.map((part, index) => (
    <Paper key={message.id + "part" + index}>
      <Group justify="space-between">
        <Group>
          <Text fz="sm">Part {index + 1} </Text>
          <Badge size="xs" color="grape" radius="sm" variant="light">
            {part.type}
          </Badge>
        </Group>

        {part.type === "text" && (
          <Group>
            <Badge size="xs" color="blue" radius="sm" variant="light">
              <NumberFormatter thousandSeparator value={part.text?.length} />{" "}
              characters
            </Badge>

            <CopyTextActionIcon text={part.text} />
          </Group>
        )}
      </Group>

      {part.type === "text" && <ContentPartText part={part} />}
      {part.type === "functionCall" && <ContentPartFunctionCall part={part} />}
      {part.type === "functionResult" && (
        <ContentPartFunctionResult part={part} />
      )}
      {part.type === "image" && (
        <ContentPartImage part={part} request={request} />
      )}
    </Paper>
  ));
}

export default function InferenceRequest({
  request,
  messages,
  messageOffset = 0,
}) {
  const displayMessages = messages || request.messages;
  return (
    <Paper>
      <Stack gap="xs">
        {displayMessages?.map((q, idx) => (
          <Paper
            radius={0}
            p={4}
            style={{ borderTop: "1px dotted gray" }}
            key={request.id + "req" + (messageOffset + idx)}
          >
            <Group mb="xs">
              <Text fz="sm" fw={500}>
                Message {messageOffset + idx + 1}{" "}
              </Text>
              <Badge size="xs" color="teal" radius="sm" variant="dot">
                {q.role}
              </Badge>

              <Badge size="xs" color="gray" radius="sm" variant="light">
                <NumberFormatter
                  thousandSeparator
                  value={q.content?.length ?? 1}
                />{" "}
                parts
              </Badge>
            </Group>

            {/* CONSIDER ADDING A REQUEST CONTEXT TO AVOID EXCESSIVE PROP-DRILLING */}
            <MessageDetails message={q} request={request} />
          </Paper>
        ))}
      </Stack>
    </Paper>
    // </Paper>
  );
}
